/**
 * Utility functions for formatting data display
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Format number as currency (NZD)
 */
export function formatCurrency(
  amount: number,
  options?: { compact?: boolean }
): string {
  const formatter = new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    notation: options?.compact ? 'compact' : 'standard',
    compactDisplay: 'short',
  });

  return formatter.format(amount);
}

/**
 * Format large numbers with K/M suffixes
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toFixed(0);
}

/**
 * Format date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Format date as human-readable string
 */
export function formatDate(date: Date | string, formatStr = 'PPP'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format days as text (e.g., "30 days", "1 day")
 */
export function formatDays(days: number): string {
  if (days === 1) return '1 day';
  return `${days} days`;
}
